import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { Button } from "@/components/ui/button";
import { useNavigate, Link } from "react-router-dom";
import { Activity, DollarSign, Home, Plus, Users } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import Header from "@/components/layout/Header";
import { getBrokerProperties, getBrokerPropertyStats } from "@/services/property";
import { getInquiryStats, getInquiries } from "@/services/inquiry";
import { PropertyType } from "@/types/property";
import { format } from "date-fns";
import { pagination } from "@/constants";
import { encodeS3Url } from "@/components/encodeS3Url";
import { capitalizeWords } from '@/helper';

interface Inquiry {
  _id: string;
  contactInfo: {
    name: string;
    email: string;
    phone: string;
  };
  user?: {
    name: string;
  };
  property: {
    specifications: {
      titleDeed: string;
    };
  };
  message: string;
  createdAt: string;
}

const BrokerDashboard = () => {
  const navigate = useNavigate();
  const [brokerName, setBrokerName] = useState("");
  const [stats, setStats] = useState({
    totalProperties: 0,
    published: 0,
    rejected: 0,
    pending: 0,
  });
  const [recentProperties, setRecentProperties] = useState<PropertyType[]>([]);
  const [inquiryStats, setInquiryStats] = useState({
    totalInquiries: 0,
    pendingInquiries: 0,
    respondedInquiries: 0
  });
  const [recentInquiries, setRecentInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const authUserStr = Cookies.get("authUser");
    if (authUserStr) {
      const user = JSON.parse(authUserStr);
      setBrokerName(user.name || user.email || "Broker");
    }

    const fetchStats = async () => {
      setLoading(true);
      try {
        const [statsData, propertiesData, inquiryData, inquiriesList] = await Promise.all([
          getBrokerPropertyStats(),
          getBrokerProperties({
            page: pagination.page,
            limit: pagination.recentLimit,
            approvalStatus: 'PUBLISHED'
          }),
          getInquiryStats(),
          getInquiries({
            page: pagination.page,
            limit: pagination.recentLimit,
          })
        ]);

        setStats(statsData);
        setInquiryStats(inquiryData);

        if (Array.isArray(propertiesData.data)) {
          setRecentProperties(propertiesData.data || []);
        } else {
          console.error("Invalid properties data format:", propertiesData.data);
        }

        if (Array.isArray(inquiriesList.data)) {
          setRecentInquiries(inquiriesList.data || []);
        } else {
          console.error("Invalid inquiries data format:", inquiriesList.data);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="pt-32 pb-20 bg-secondary/30 flex-1">

        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold">Broker Dashboard</h1>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-muted-foreground">Welcome back,</span>
                {loading && !brokerName ? (
                  <Skeleton className="h-4 w-24" />
                ) : (
                  <span className="font-medium text-foreground">{capitalizeWords(brokerName)}</span>
                )}
              </div>
            </div>
            <div className="mt-4 md:mt-0 flex gap-3">
              <Button variant="outline" onClick={() => navigate("/broker/properties")}>
                <Home className="mr-2 h-4 w-4" /> Properties
              </Button>
              <Button variant="outline" onClick={() => navigate("/broker/leads")}>
                <Users className="mr-2 h-4 w-4" /> Leads
              </Button>
              <Button onClick={() => navigate("/broker/property-upload")}>
                <Plus className="mr-2 h-4 w-4" /> Add Property
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              loading ? (
                <Card key={i}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-4 rounded-full" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-8 w-16 mb-2" />
                    <Skeleton className="h-3 w-32" />
                  </CardContent>
                </Card>
              ) : (
                <Card key={i}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      {i === 1 ? "Total Properties" : i === 2 ? "Active Listings" : i === 3 ? "Pending" : "Leads"}
                    </CardTitle>
                    {i === 1 ? <Home className="h-4 w-4 text-muted-foreground" /> :
                      i === 2 ? <Activity className="h-4 w-4 text-muted-foreground" /> :
                        i === 3 ? <DollarSign className="h-4 w-4 text-muted-foreground" /> :
                          <Users className="h-4 w-4 text-muted-foreground" />}
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {i === 1 ? stats.totalProperties : i === 2 ? stats.published : i === 3 ? stats.pending : inquiryStats.totalInquiries}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {i === 1 ? "Total listed properties" : i === 2 ? "Properties currently live" : i === 3 ? "Properties pending approval" : "Total inquiries received"}
                    </p>
                  </CardContent>
                </Card>
              )
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Recent Properties</CardTitle>
                <CardDescription>Your most recently added properties</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {loading ? (
                    [1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center space-x-4 rounded-md border p-4">
                        <Skeleton className="h-12 w-12 rounded-md" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-4 w-3/4" />
                          <Skeleton className="h-3 w-1/2" />
                        </div>
                        <Skeleton className="h-4 w-16" />
                      </div>
                    ))
                  ) : recentProperties.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No properties added yet.</p>
                  ) : (
                    recentProperties.map((property) => (
                      <Link
                        to={`/properties/${property.slug}`}
                        key={property._id}
                        className="flex items-center space-x-4 rounded-md border p-4 hover:bg-accent/50 transition-colors"
                      >
                        <div className="h-12 w-12 rounded-md bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0 bg-muted">
                          {property.media?.images?.[0] ? (
                            <img
                              src={encodeS3Url(property.media.images[0])}
                              alt={property.specifications?.titleDeed || "Property"}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <Home className="h-6 w-6 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1 space-y-1 min-w-0">
                          <p className="text-sm font-medium truncate">{capitalizeWords(property.specifications?.titleDeed || property.title || '')}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {property.location?.address || 'N/A'} • Added {format(new Date(property.createdAt), 'MMM dd, yyyy')}
                          </p>
                        </div>
                        <div className="text-sm font-medium whitespace-nowrap">
                          {new Intl.NumberFormat('en-AE', {
                            style: 'currency',
                            currency: property.pricing?.currency || 'AED',
                            maximumFractionDigits: 0,
                          }).format(property.pricing?.amount || 0)}
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Recent Inquiries</CardTitle>
                <CardDescription>Latest inquiries from potential buyers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {loading ? (
                    [1, 2, 3].map((i) => (
                      <div key={i} className="flex flex-col space-y-2 rounded-md border p-4">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                        <Skeleton className="h-3 w-1/4" />
                        <Skeleton className="h-3 w-full mt-2" />
                      </div>
                    ))
                  ) : recentInquiries.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No inquiries yet.</p>
                  ) : (
                    recentInquiries.map((inquiry) => (
                      <div key={inquiry._id} className="flex flex-col space-y-1 rounded-md border p-4">
                        <p className="text-sm font-medium">Client: {inquiry.contactInfo?.name || inquiry.user?.name || 'Guest'}</p>
                        <p className="text-xs">{capitalizeWords(inquiry.property?.specifications?.titleDeed || 'General Inquiry')}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(inquiry.createdAt), 'MMM dd, yyyy')}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {inquiry.message}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrokerDashboard;
